import React, { useCallback } from 'react';
import { LEASING_EXPENSE_TYPES_QUERY } from 'features/leasingExpenseType/queries';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { leasingExpenseTypesStore } from '../store';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_EXPENSE_TYPES_READ } from 'constants/rightsCodes';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_LEASING_EXPENSE_TYPE } from '../mutations';
import { LEASING_EXPENSE_TYPE_DELETED } from '../../subscriptions';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import LeasingExpenseTypeComponent from './LeasingExpenseTypeComponent';
import useSubscription from 'hooks/apollo/useSubscription';

const LeasingExpenseTypesListBrowser = () => {
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const { t } = useTranslation();

  const canRead = usePermission(ADMIN_LEASING_EXPENSE_TYPES_READ);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: LEASING_EXPENSE_TYPES_QUERY,
        name: 'leasingExpenseTypes'
      }
    }),
    []
  );

  const columns = useMemo(
    () => [
      { name: 'expenseCode', caption: 'LeasingExpenseType.ExpenseCode', width: 10 },
      { name: 'expenseName', caption: 'LeasingExpenseType.ExpenseName', width: 10 },
      { name: 'currencyValue', caption: 'LeasingExpenseType.CurrencyValue', width: 4 },
      { name: 'currencyCode', caption: 'LeasingExpenseType.Currency', width: 4 },
      { name: 'currencyPercent', caption: 'LeasingExpenseType.CurrencyPercent', width: 4 },
      { name: 'isActive', caption: 'LeasingExpenseType.IsActive', width: 5 },
      { name: 'actions', caption: 'Actions', width: 10, align: 'right' }
    ],
    []
  );

  // #region Delete

  useSubscription(LEASING_EXPENSE_TYPE_DELETED, {
    onData: ({ client }) => {
      addToast(t('LeasingExpenseType.LeasingExpenseTypeDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingExpenseTypes' });
      client.cache.gc();
    }
  });

  const [deleteLeasingExpenseType] = useMutation(DELETE_LEASING_EXPENSE_TYPE);

  const handleDelete = useCallback(
    expenseTypeId => {
      confirm(
        () => {
          deleteLeasingExpenseType({ variables: { expenseTypeId } });
        },
        {
          title: t('LeasingExpenseType.DeleteLeasingExpenseType'),
          content: t('LeasingExpenseType.AreYouSureDelete')
        }
      );
    },
    [deleteLeasingExpenseType, confirm, t]
  );

  // #endregion

  const itemComponent = useCallback(({ data }) => <LeasingExpenseTypeComponent data={data} onDelete={handleDelete} />, [handleDelete]);

  return (
    <Card icon={ListAltIcon}>
      {canRead && (
        <>
          <TableListBrowser
            name='leasing-expense-types-browser'
            store={leasingExpenseTypesStore}
            ItemComponent={itemComponent}
            columns={columns}
          />
          <ExportModalComponent store={leasingExpenseTypesStore} options={fetchingOptions} />
          <ApolloFetchingComponent store={leasingExpenseTypesStore} options={fetchingOptions} />
        </>
      )}
    </Card>
  );
};

export default LeasingExpenseTypesListBrowser;
